import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo_eagle from "./assets/img/logo_eagle.png";
import { FaPenSquare, FaTrashAlt } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import "./dSpr.css";
import "./Project_configuration.css";

function Project_configuration({ navSize }) {
    const [projects, setProjects] = useState([]);
    const [newProjectName, setNewProjectName] = useState("");
    const [editProject, setEditProject] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState("");
    const [editTask, setEditTask] = useState(null);
    const navigate = useNavigate();

    // Fetch all projects on load
    useEffect(() => {
        fetchProjects();
        fetchloginedEmployees();
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
             
            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const fetchProjects = async () => {
        try {
            const res = await apiFetch("/api/Project_configuration/all");
            const raw = await res.json();
            const data = Array.isArray(raw) ? raw : [];
            setProjects(data);

            if (data.length > 0) {
                fetchTasks(data[0].project_id); // ✅ Auto-select first project
            }
        } catch (err) {
            console.error("Error fetching projects:", err);
        }
    };

    const addProject = async () => {
        if (!newProjectName.trim()) { dnAlert.warning("Missing", "Enter project name"); return; }

        const res = await apiFetch("/api/Project_configuration/add", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ project_name: newProjectName }),
            credentials: 'include',
        });

        const data = await res.json();
        if (data.success) {
            setNewProjectName("");
            fetchProjects();
        }
    };

    const updateProject = async () => {
        const res = await apiFetch(`/api/Project_configuration/update/${editProject.auto_id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ project_name: editProject.project_name }),
            credentials: 'include',
        });

        const data = await res.json();
        if (data.success) {
            setEditProject(null);
            fetchProjects();
        }
    };

    const deleteProject = async (auto_id) => {
        const ok = await dnAlert.confirm({
            title: "Delete project?",
            message: "This project will be permanently deleted.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        await apiFetch(`/api/Project_configuration/delete/${auto_id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
        });
        dnAlert.success("Deleted", "Project has been deleted.");
        fetchProjects();
    };

    // 🔹 Fetch tasks for a selected project
    const fetchTasks = async (project_id) => {
        setSelectedProject(project_id);
        const res = await apiFetch(`/api/Project_configuration/tasks/${project_id}`);
        const data = await res.json();

        // ✅ Ensure tasks is always an array
        setTasks(Array.isArray(data) ? data : []);
    };

    // 🔹 Add New Task
    const addTask = async () => {
        if (!newTask.trim()) { dnAlert.warning("Missing", "Enter task description"); return; }
        await apiFetch("/api/Project_configuration/tasks/add", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ project_id: selectedProject, task_desc: newTask }),
            credentials: 'include',
        });

        setNewTask("");
        fetchTasks(selectedProject);
    };

    // 🔹 Update Task
    const updateTask = async () => {
        await apiFetch(`/api/Project_configuration/tasks/update/${editTask.auto_id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ task_desc: editTask.task_desc }),
            credentials: 'include',
        });

        setEditTask(null);
        fetchTasks(selectedProject);
    };

    // 🔹 Delete Task
    const deleteTask = async (auto_id) => {
        const ok = await dnAlert.confirm({
            title: "Delete task?",
            message: "This task will be permanently deleted.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        await apiFetch(`/api/Project_configuration/tasks/delete/${auto_id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
        });
        dnAlert.success("Deleted", "Task has been deleted.");
        fetchTasks(selectedProject);
    };

    // Convert ISO date string to "YYYY-MM-DD HH:mm:ss"
    const formatDateTime = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
        const day = String(date.getDate()).padStart(2, "0");

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    return (
        <div className="project-details-container">
            <div className={`project-details-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{
                                    height: "50px",
                                    objectFit: "contain",
                                    marginLeft: "-20px",
                                }}
                            />
                        </a>
                        Project Configuration
                    </h2>
                    <p className="dspr-subtitle">Maintain the project + task catalogue that powers dTime entries and timesheet selection.</p>
                </div>

                <div className={`project-details-content`}>
                    <div className="add-project">
                        <input
                            type="text"
                            placeholder="Enter project name"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                        />
                        <button onClick={addProject}>Add Project</button>
                    </div>

                    <table className="project-table">
                        <thead>
                            <tr>
                                <th>Project ID</th>
                                <th>Project Name</th>
                                <th>Created By</th>
                                <th>Created Time</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((proj) => (
                                <tr
                                    key={proj.auto_id}
                                    onClick={() => fetchTasks(proj.project_id)}
                                    className={selectedProject === proj.project_id ? "selected-row" : ""}
                                >
                                    <td>{proj.project_id}</td>
                                    <td>
                                        {editProject?.auto_id === proj.auto_id ? (
                                            <input
                                                type="text"
                                                value={editProject.project_name}
                                                onChange={(e) =>
                                                    setEditProject({
                                                        ...editProject,
                                                        project_name: e.target.value,
                                                    })
                                                }
                                            />
                                        ) : (
                                            proj.project_name
                                        )}
                                    </td>
                                    <td>{proj.created_by}</td>
                                    <td>{formatDateTime(proj.created_time)}</td>
                                    <td>
                                        {editProject?.auto_id === proj.auto_id ? (
                                            <>
                                                <button className="pc-text-btn pc-text-btn--save"   onClick={updateProject}>Save</button>
                                                <button className="pc-text-btn pc-text-btn--cancel" onClick={() => setEditProject(null)}>Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="pc-icon-btn"            title="Edit"   onClick={() => setEditProject(proj)}><FaPenSquare /></button>
                                                <button className="pc-icon-btn pc-icon-btn--danger" title="Delete" onClick={() => deleteProject(proj.auto_id)}><FaTrashAlt /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* TASK SECTION BELOW */}
                    {selectedProject && (
                        <div className="task-section">
                            <h2>Tasks for {selectedProject}</h2>

                            <div className="add-project">
                                <input
                                    type="text"
                                    placeholder="Enter task description"
                                    value={newTask}
                                    onChange={(e) => setNewTask(e.target.value)}
                                />
                                <button onClick={addTask}>Add Task</button>
                            </div>

                            <table className="project-table">
                                <thead>
                                    <tr>
                                        <th>Task ID</th>
                                        <th>Task Description</th>
                                        <th>Created By</th>
                                        <th>Created Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(tasks) && tasks.length > 0 ? (
                                        tasks.map((task) => (
                                            <tr key={task.auto_id}>
                                                <td>{task.task_id}</td>
                                                <td>
                                                    {editTask?.auto_id === task.auto_id ? (
                                                        <input
                                                            type="text"
                                                            value={editTask.task_desc}
                                                            onChange={(e) =>
                                                                setEditTask({ ...editTask, task_desc: e.target.value })
                                                            }
                                                        />
                                                    ) : (
                                                        task.task_desc
                                                    )}
                                                </td>
                                                <td>{task.created_by}</td>
                                                <td>{formatDateTime(task.created_time)}</td>
                                                <td>
                                                    {editTask?.auto_id === task.auto_id ? (
                                                        <>
                                                            <button className="pc-text-btn pc-text-btn--save"   onClick={updateTask}>Save</button>
                                                            <button className="pc-text-btn pc-text-btn--cancel" onClick={() => setEditTask(null)}>Cancel</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="pc-icon-btn"            title="Edit"   onClick={() => setEditTask(task)}><FaPenSquare /></button>
                                                            <button className="pc-icon-btn pc-icon-btn--danger" title="Delete" onClick={() => deleteTask(task.auto_id)}><FaTrashAlt /></button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: "center", padding: "10px", color: "gray" }}>
                                                No tasks available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

}

export default Project_configuration;
